// Path: ./src/app/pages/CreateFlyerItemPage/index.tsx
import React, { useEffect, useState, useCallback } from "react";
import { SearchInputWithAdvanced } from "../../components/SearchInputWithAdvanced";
import FilterBar from "../../components/FilterBar";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { useFlyerItemKeyboardNavigation } from "../../../hooks/useFlyerItemKeyboardNavigation";
import { FlyerGridList } from "../../components/FlyerGridList";
import { adminApiService } from "../../../utils/api";
import {
  GfFlyerItemDto,
  GfImage,
  GfMultiImage,
  SortDirection,
  UpdateGfFlyerItemDto,
  AdvancedSearchConfigDto,
  AdvancedSearchGfImageDto,
  GfFlyerItemListDto,
  CoordinateJobStatusResponseDto,
  JobTestingRecord,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";
import {
  Stack,
  Typography,
  Tooltip,
  Chip,
  Box,
  CircularProgress,
  Button,
  Alert,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import HideImageOutlinedIcon from "@mui/icons-material/HideImageOutlined";
import HeightIcon from "@mui/icons-material/Height";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import {
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { SideFlyerGridList } from "../../components/SideFlyerGridList";
import DeleteIcon from "@mui/icons-material/Delete";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { extractEnglishAndChineseV2 } from "../../../utils/utils";
import { view_createFlyer_page } from "../../../utils/analytics";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useLoading } from "../../../contextApi/LoadingContext";

import {
  FlyerEditorTooltipDescription,
  NO_IMAGE_AVAILABLE,
  SUPERADMIN,
} from "../../../utils/constant/stringConstant";
import { useFlyerItemTableColumns } from "./useFlyerItemTableColumns";
import {
  Container,
  LeftSideTop,
  LeftSideBottom,
  DateTableWrapper,
  LeftButtonSection,
  TopApp,
  CreateFlyerButtonWrapper,
  actionButtonSx,
} from "./styled";
import { useNavigate, useParams } from "react-router-dom";
import CreateFlyerModalWithMetaData from "./CreateFlyerModal/createFlyerItemWithMetaData";
import { ButtonComponent } from "../../components/Button";
import { useFlyerGenerationHandlers } from "./hooks/useFlyerGenerationHandlers";
import { useResizableSplitPane } from "./hooks/useResizableSplitPane";
import TemplateSelectionModal from "./TemplateModals/TemplateSelectionModal";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import LinearDeterminate from "../../components/LinearComponent";
import GfGeneratedFlyerImagesModal from "./GfGeneratedFlyerImagesModal";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import {
  safeSessionStorageGetItem,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetItem,
} from "../../../utils/localStorage";
import { exportExcelFnV2 } from "./Excel/generateExcel";
import { ExportCategoriesModal } from "./Excel/ExportCategoriesModal";
import { ImportCategoriesModal } from "./Excel/ImportCategoriesModal";
import { importExcelFn } from "./Excel/importExcel";
import { useFlyerItemCategories, useAllFlyerItems } from "../../../hooks";
import { routes } from "../../../utils/constant/navigationRoutes";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { useTableSettingsConfig } from "../../../hooks/useTableSettingsConfig";
import { SettingKey } from "@swagger/typescript-fetch-goflyer";
import { initialGridPaginationModel } from "../../../utils/constant/initialGridPaginationModel";
import { ToolsModal } from "../../components/ToolsModal";
import { StatisticsModal } from "../../components/StatisticsModal";
import ImageTagEditModal from "../../components/ImageTagEditModal";
import { exportOnlySelectedFlyerItems } from "./Excel/exportSeleteItems";
import { flyerItemFieldsWithoutDuplicateIdentity } from "./utils/flyerItemFieldsWithoutDuplicateIdentity";
import { useImageFilters } from "./hooks/useImageFilters";
import { useAdvancedSearchConfig } from "../../../hooks/useAdvancedSearchConfig";
import ResizableDraggableWindow from "../../components/ResizableDraggableWindow";
import FlyerCoordinateEditor from "../../../components/FlyerCoordinateEditor";
import { CoordinateChange } from "../../../components/FlyerCoordinateEditor/types";
import { handleCoordinateBatchSave as processBatchSave } from "./utils/coordinateBatchSave";
import MerchantInfoSection from "../../components/MerchantInfoSection";

export interface SearchDataProps {
  startDate?: Date;
  endDate?: Date;
  search?: string;
  category?: string;
  advancedConfig?: AdvancedSearchConfigDto;
}

interface gfImageDataProps {
  gfImageList?: GfImage[];
  page: number;
  total?: number;
}

interface flyerItemsListProps {
  rows?: GfFlyerItemDto[];
  columns?: GridColDef<GfFlyerItemDto>[];
  total?: number;
  perPage?: number; //TODO, not good
  page?: number; //TODO, not good
}

const SPLIT_RATIO_SESSION_KEY = "createFlyer:leftPaneRatio";
const CreateFlyer = () => {
  const { showLoadingScreen } = useLoading();
  const { t } = useTranslation();
  const authHeaders = useAuthHeaders();
  const navigate = useNavigate();
  const { flyerItemListId } = useParams();
  const { state } = React.useContext(GoFlyerAdminContext);
  const { flyerItemCategories } = useFlyerItemCategories();
  const { pageLimit } = useTableSettingsConfig(SettingKey.TableFlyerItems);
  const { paginationModel, sortModel, handleTableChange } =
    useQueryParams(pageLimit);
  const advancedSearchConfig = useAdvancedSearchConfig();

  const [flyerItems, setFlyerItems] = useState<flyerItemsListProps>({
    rows: [],
  });
  const [updateFlyerItem, setUpdateFlyerItem] =
    useState<UpdateGfFlyerItemDto>();
  const [flyerItemListData, setFlyerItemListData] =
    useState<GfFlyerItemListDto | null>(null);
  const [currentFlyerItemIndex, setCurrentFlyerItemIndex] =
    useState<number>(-1);
  const [navigationDirection, setNavigationDirection] = useState<
    "left" | "right" | null
  >(null);
  const [gfImageData, setGfImageData] = useState<gfImageDataProps>({
    page: 1,
    gfImageList: [],
    total: 0,
  });
  const [rightSideHasMoreGfImage, setRightSideHasMoreGfImage] = useState(false);
  const [jobId, setJobId] = useState<string>();

  const [openCreateFlyer, setOpenCreateFlyer] = useState<boolean>(false);
  const [openDownloadFlyers, setOpenDownloadFlyers] = useState(false);
  const [openExportModal, setOpenExportModal] = useState<boolean>(false);
  const [openImportModal, setOpenImportModal] = useState<boolean>(false);

  const [openToolsModal, setOpenToolsModal] = useState(false);
  const [openStatisticsModal, setOpenStatisticsModal] = useState(false);
  const [imageForTagEdit, setImageForTagEdit] = useState<GfImage | null>(null);
  const [openResizableWindow, setOpenResizableWindow] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 900, height: 700 });
  const [jobInProgressModal, setJobInProgressModal] = useState(false);

  const [createItemCallback, setCreateItemCallback] = useState<
    ((item: GfFlyerItemDto) => void) | null
  >(null);
  const [activeWindow, setActiveWindow] = useState<
    "modal" | "resizable" | null
  >(null);

  const [isRightSideImageLoading, setIsRightSideImageLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  // Guards against out-of-order async responses overwriting newer results
  const searchSeq = React.useRef(0);

  const [searchData, setSearchData] = useState<SearchDataProps>();
  const [clearSearch, setClearSearch] = useState(false);
  const {
    splitPaneRef,
    leftPaneRatio,
    minLeftPaneWidth,
    minRightPaneWidth,
    handleResizeStart,
    handleResizeTouchStart,
  } = useResizableSplitPane({
    sessionKey: SPLIT_RATIO_SESSION_KEY,
    minLeftPaneWidth: 560,
    minRightPaneWidth: 360,
  });

  // Fetch all flyer items for coordinate mapping modal (not paginated)
  // Note: refetchAllFlyerItems() resets cache, so calling it after creating
  // a new item will ensure fresh data when modal reopens
  const {
    allFlyerItems,
    refetch: refetchAllFlyerItems,
    isLoading: isLoadingAllItems,
    error: allFlyerItemsError,
    updateItem: updateAllFlyerItem,
    reset: resetAllFlyerItems,
  } = useAllFlyerItems({
    flyerItemListId,
    authHeaders,
    enabled: openResizableWindow,
  });

  const { filtersState, selectList } = useImageFilters(
    flyerItemCategories,
    () => {
      // Reset image data when filters change
      setGfImageData({
        page: 1,
        gfImageList: [],
        total: 0,
      });
    },
  );

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const {
    isDeleteFlow,
    setIsDeleteFlow,
    generateFlyersForFlyerItemList,
    handleModalConfirmation: handleModalConfirmationFromHook,
    openTemplateModal,
    setOpenTemplateModal,
    isGenerating,
    open,
    setOpen,
    confirmationModal,
    setConfirmationModal,
    pendingTemplate,
    generateFlyers,
    setGenerateFlyers,
    flyerGenerationInProgress,
    setFlyerGenerationInProgress,
  } = useFlyerGenerationHandlers({
    flyerItemListId,
    flyerTotal: flyerItems?.total,
    setJobId,
  });

  const handleCancel = () => {
    setOpen(false);
    setGenerateFlyers(false);
    setIsDeleteFlow(false);
  };

  const handleToolsClick = () => {
    setOpenToolsModal(true);
  };

  const handleStatisticsClick = () => {
    setOpenStatisticsModal(true);
  };

  const handleOpenDelete = () => {
    setOpen(true);
    setIsDeleteFlow(true);
    setGenerateFlyers(false);
    setConfirmationModal({
      title: `${t(messages.Delete_Confirmation())}`,
      description: `
      ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
        messages.Selected_Flyer_Items(),
        { count: rowSelectionModel.length },
      )}?  ${t(messages.This_action_cannot_be_undone())}.
      `,
      warningMessage: "",
      warningSeverity: "warning",
    });
  };

  const handleGenerateFlyers = async () => {
    if (!flyerItemListId) return;
    setOpenTemplateModal(true);
  };

  /**
   * when user first time create flyer item and change page.
   * //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) we can just use infinite scroll here, don't need to use pagination to complicate situation here
   * @param event
   * @param value
   */
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setGfImageData({
      ...gfImageData,
      page: value,
      gfImageList: [],
      total: 0,
    });
  };

  const handleRightSidePageChangeScroll = useCallback(() => {
    setGfImageData((prev) => ({
      ...prev,
      page: prev.page + 1,
    }));
  }, []);
  /**
   * 1. when user click enter after type something in the search box
   * 2. also trigger when user start search in the pop up image search box
   */
  const handleOnImageSearchClick = useCallback(
    (value: string, advancedConfig?: AdvancedSearchConfigDto) => {
      if (
        value?.trim().toLowerCase() !==
          searchData?.search?.trim().toLowerCase() ||
        JSON.stringify(advancedConfig) !==
          JSON.stringify(searchData?.advancedConfig)
      ) {
        setRightSideHasMoreGfImage(false);
        setSearchData((prev) => ({
          ...prev,
          search: value?.trim(),
          advancedConfig,
        }));
        setGfImageData({
          page: 1,
          gfImageList: [],
          total: 0,
        });
        setClearSearch(false);
      }
    },
    [searchData],
  );

  /**
   * 1. start searching image when first enter the app
   * 2. also search when user search image in the pop up search image box
   * @param value
   * @param page
   * @param perPage
   * @param category
   * @param format
   * @param size
   * @param date
   * @param advancedConfig
   */
  const handleImageSearch = async (
    value: string,
    page: number,
    perPage: number,
    category: string[],
    format: string[],
    size?: SortDirection,
    date?: SortDirection,
    advancedConfig?: AdvancedSearchConfigDto,
  ) => {
    if (value === "") {
      setGfImageData({
        page: 1,
        gfImageList: [],
        total: 0,
      });
      setRightSideHasMoreGfImage(false);
      setIsRightSideImageLoading(false);
      return;
    }
    try {
      setRightSideHasMoreGfImage(false);
      setIsRightSideImageLoading(true);

      // increment search sequence to detect stale responses
      const seq = ++searchSeq.current;

      const shouldContain = category?.length
        ? Array.from(
            new Set(
              category.flatMap((val: string) => {
                const lowerCase = val.toLowerCase();
                const sentenceCase =
                  val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
                return [
                  val,
                  lowerCase,
                  sentenceCase,
                  encodeURIComponent(lowerCase),
                  encodeURIComponent(sentenceCase),
                ];
              }),
            ),
          )
        : undefined;

      const requestBody: AdvancedSearchGfImageDto = {
        text: value,
        page,
        limit: perPage,
        shouldContain,
        shouldNotContain: ["/flyers/", "flyers", "/menus/", "menus"],
        mediaType: format?.length ? format : undefined,
        updatedOrderBy: date,
        s3SizeOrderBy: size,
        storeIds: flyerItemListData?.stores?.map((store) => store.id) || [],
        advancedConfig: advancedConfig,
        searchFlyerItems: advancedSearchConfig.searchFlyerItems,
      };
      const response = advancedConfig
        ? await adminApiService.gfImageAdminControllerSearchImageWithAdvancedConfig(
            requestBody,
            authHeaders,
          )
        : await adminApiService.gfImageAdminControllerSearchImageWithFilters(
            value,
            page,
            perPage,
            requestBody.shouldContain,
            requestBody.shouldNotContain,
            requestBody.mediaType,
            date,
            size,
            requestBody.storeIds,
            advancedSearchConfig.searchFlyerItems,
            authHeaders,
          );

      if (seq === searchSeq.current) {
        const total = response?.total || 0;
        const hasMore = page * perPage < total;
        setRightSideHasMoreGfImage(hasMore);
        setGfImageData((prev) => ({
          ...prev,
          total,
          gfImageList: [
            ...(page === 1 ? [] : prev.gfImageList || []),
            ...response.data,
          ],
        }));
      }
    } catch (error) {
      console.log("handleImageSearch error", error);
    } finally {
      setIsRightSideImageLoading(false);
    }
  };

  /**
   * 1. click a search image result
   * 2. click a search image result in the pop up image search
   * @param value
   */
  const handleOnImageItemClick = useCallback(
    async (value: GfImage) => {
      console.log("Image item clicked:", value);
      if (value?.src) {
        showLoadingScreen(true);
        console.log(
          "Image source exists, proceeding with extraction and API call.",
        );
        const TextResult = extractEnglishAndChineseV2(
          value?.fileName || value?.alt || "",
        );
        console.log("Extracted Text Result:", TextResult);
        console.log("Fetching flyer item by image ID:", value.id || "");
        const flyerItemResponse =
          await adminApiService.gfFlyerItemAdminControllerFindFlyerItemByImageAndStore(
            value.id,
            flyerItemListData?.stores[0]?.id,
            authHeaders,
          );

        console.log("Flyer item response received:", flyerItemResponse);

        const flyerItem =
          flyerItemFieldsWithoutDuplicateIdentity(flyerItemResponse);

        if (
          !flyerItem?.unit &&
          flyerItems?.rows?.length &&
          searchData?.search?.trim()
        ) {
          const searchTerm = searchData.search.trim().toLowerCase();

          const found = flyerItems.rows.find((row) =>
            row.gfMultiImages
              ?.map((val) => val.gfImage)
              .some((image) =>
                image.alt?.trim().toLowerCase().includes(searchTerm),
              ),
          );

          if (found) {
            flyerItem.unit = found.unit;
            flyerItem.unitV2 = found.unitV2;
          }
        }
        console.log("Extracted flyer item details:", flyerItem);
        const multiImages = {
          gfImage: value,
          gfFlyerItem: updateFlyerItem,
          id: value.id,
        } as GfMultiImage;
        setUpdateFlyerItem((prev) => ({
          ...flyerItem,
          name: flyerItem?.name || TextResult?.english,
          nameChinese: flyerItem?.nameChinese || TextResult?.chinese,
          gfFlyerItemCategory: flyerItem?.gfFlyerItemCategory,
          gfMultiImages: [multiImages],
          flyerItemList: prev?.flyerItemList,
        }));
        console.log("Updated flyer item state:", flyerItem);

        showLoadingScreen(false);
      } else {
        setUpdateFlyerItem((prev) => ({
          flyerItemList: prev?.flyerItemList,
        }));
      }
      setOpenCreateFlyer(true);
      setActiveWindow("modal");
    },
    [
      flyerItems.rows,
      searchData?.search,
      authHeaders,
      updateFlyerItem,
      showLoadingScreen,
      createItemCallback,
    ],
  );

  /**
   * when user click the background area to close the flyer item modal window
   */
  const handleOnCloseFlyer = () => {
    setOpenCreateFlyer(false);
    setNavigationDirection(null);
    setCurrentFlyerItemIndex(-1);
    setActiveWindow(openResizableWindow ? "resizable" : null);
  };

  /**
   * Handles the click event to edit a flyer item.
   * Retrieves the data for the flyer item, prepares it for editing,
   * and opens the flyer item creation modal in edit mode.
   * @param value - The flyer item data to be edited.
   */
  const handleOnClickEdit = React.useCallback(
    async (value: GfFlyerItemDto, indexOverride?: number) => {
      if (indexOverride === undefined) {
        showLoadingScreen(true);
      }

      setUpdateFlyerItem({
        ...value,
      });

      if (indexOverride !== undefined) {
        setCurrentFlyerItemIndex(indexOverride);
      } else {
        const index =
          flyerItems.rows?.findIndex((item) => item.id === value.id) ?? -1;
        setCurrentFlyerItemIndex(index);
      }

      setOpenCreateFlyer(true);
      showLoadingScreen(false);
    },
    [showLoadingScreen, flyerItems.rows],
  );

  const handleNavigateToPrevious = useCallback(async () => {
    if (navigationDirection !== null || !updateFlyerItem?.id?.length) return;

    if (currentFlyerItemIndex > 0 && flyerItems.rows) {
      const previousItem = flyerItems.rows[currentFlyerItemIndex - 1];
      setNavigationDirection("left");
      handleOnClickEdit(previousItem, currentFlyerItemIndex - 1);
    }
  }, [
    currentFlyerItemIndex,
    flyerItems.rows,
    handleOnClickEdit,
    navigationDirection,
    updateFlyerItem,
  ]);

  const handleNavigateToNext = useCallback(async () => {
    if (navigationDirection !== null || !updateFlyerItem?.id?.length) return;

    if (
      currentFlyerItemIndex >= 0 &&
      flyerItems.rows &&
      currentFlyerItemIndex < flyerItems.rows.length - 1
    ) {
      const nextItem = flyerItems.rows[currentFlyerItemIndex + 1];

      console.log("nextItem", nextItem);
      setNavigationDirection("right");
      handleOnClickEdit(nextItem, currentFlyerItemIndex + 1);
    }
  }, [
    currentFlyerItemIndex,
    flyerItems.rows,
    handleOnClickEdit,
    navigationDirection,
  ]);

  useEffect(() => {
    if (!navigationDirection) return;

    const handleAnimationEnd = () => {
      setNavigationDirection(null);
    };

    const modalWrapper = document.querySelector(
      '[data-testid="create-flyer-item-modal"]',
    );
    if (modalWrapper) {
      modalWrapper.addEventListener("animationend", handleAnimationEnd);

      return () => {
        modalWrapper.removeEventListener("animationend", handleAnimationEnd);
      };
    }
  }, [navigationDirection]);

  useFlyerItemKeyboardNavigation({
    isOpen: openCreateFlyer,
    onNavigatePrevious: handleNavigateToPrevious,
    onNavigateNext: handleNavigateToNext,
    canNavigatePrevious: currentFlyerItemIndex > 0,
    canNavigateNext:
      currentFlyerItemIndex >= 0 && flyerItems.rows
        ? currentFlyerItemIndex < flyerItems.rows.length - 1
        : false,
  });

  const handleRefreshData = React.useCallback(
    async (itemId?: string) => {
      if (!itemId) return;
      showLoadingScreen(true);

      try {
        const updatedItem =
          await adminApiService.getOneBaseGfFlyerItemAdminControllerGfFlyerItem(
            itemId,
          );

        setFlyerItems((prev) => ({
          ...prev,
          rows: prev.rows?.map((item) =>
            item.id === itemId ? updatedItem : item,
          ),
        }));
      } catch (error) {
        console.error("Error fetching updated item:", error);
      }

      showLoadingScreen(false);
    },
    [showLoadingScreen],
  );

  /**
   * when user click the confirm button to delete an flyer item
   */
  const handleFlyerItemDelete = async () => {
    if (rowSelectionModel?.length) {
      showLoadingScreen(true);

      const result = await rowSelectionModel?.map(async (value) => {
        return await adminApiService.deleteOneBaseGfFlyerItemAdminControllerGfFlyerItem(
          value as string,
          authHeaders,
        );
      }); //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) add a delete many in the backend for all admin controller

      await Promise.all(result);
      fetchFlyerItems(paginationModel, sortModel);
      await refetchAllFlyerItems();
      handleCancel();
      showLoadingScreen(false);
    }
  };

  const tableColumns = useFlyerItemTableColumns(
    handleOnClickEdit,
    handleRefreshData,
  );
  const handleExportSelectedFlyerItems = async () => {
    if (rowSelectionModel?.length) {
      setOpenExportModal(false);
      showLoadingScreen(true);

      const flyerItemsIds = rowSelectionModel.map((val) => val) as string[];
      flyerItemListId &&
        (await exportOnlySelectedFlyerItems(
          flyerItemListId,
          flyerItemsIds,
          authHeaders.headers.Authorization,
        ));
      showLoadingScreen(false);
    }
  };
  const fetchFlyerItemListDetails = async () => {
    if (flyerItemListId) {
      try {
        const response =
          await adminApiService.getOneBaseGfFlyerItemListAdminControllerGfFlyerItemList(
            flyerItemListId,
            undefined,
            ["stores.merchant.merchantLogo", "flyer.gfImages"],
            undefined,
          );
        if (
          flyerItemListId &&
          response?.validStartDate &&
          response?.validEndDate
        ) {
          setUpdateFlyerItem({
            flyerItemList: response,
          });
          setFlyerItemListData(response);
        } else {
          navigate(routes.createFlyer.base);
        }
      } catch (error) {
        console.log("Error FlyerItemListID", error);
        navigate(routes.createFlyer.base);
        showLoadingScreen(false);
      }
    }
  };

  const fetchFlyerItemsWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setIsTableLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: [
              ...activeFilterModel.items.map((item) => ({
                field: item.field,
                operator: item.operator,
                value: item.value,
                id: item.id,
              })),
              ...(flyerItemListId
                ? [
                    {
                      field: "flyerItemListId",
                      operator: "equals",
                      value: flyerItemListId,
                    },
                  ]
                : []),
            ],
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfFlyerItemAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setFlyerItems((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFlyerItemFilterModelChange = (
    newFilterModel: GridFilterModel,
  ) => {
    setFilterModel(newFilterModel);
  };

  const fetchFlyerItems = async (
    paginationModelArg: GridPaginationModel,
    gridSortModel: GridSortModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setIsTableLoading,
      async () => {
        const FlyerItemCategoryList =
          state.user?.parsedRoles?.name === SUPERADMIN
            ? []
            : state.user?.gfFlyerItemCategory?.map((value) => {
                return `${value.id}`;
              });

        return adminApiService.gfFlyerItemAdminControllerGetManyFlyerItem(
          {
            page: paginationModelArg.page,
            limit: paginationModelArg.pageSize,
            gfFlyerItemCategoryIds: FlyerItemCategoryList,
            orderBy: JSON.stringify({
              orderBy: gridSortModel?.length
                ? {
                    [`${gridSortModel[0].field}`]: `${gridSortModel[0]?.sort?.toUpperCase()}`,
                  }
                : {
                    updatedAt: "DESC",
                  },
            }),
            flyerItemListId: flyerItemListId,
          },
          authHeaders,
        );
      },
      (result) => {
        setFlyerItems((prev) => ({
          ...prev,
          rows: result.data ?? [],
          total: result.total ?? 0,
        }));
      },
    );
  };

  // Export/Import handlers
  const exportExcel = async () => {
    setOpenExportModal(true);
  };

  const handleExportWithCategories = async (selectedCategories: string[]) => {
    setOpenExportModal(false);
    showLoadingScreen(true);
    flyerItemListId &&
      (await exportExcelFnV2(
        state,
        flyerItemListId,
        sortModel,
        authHeaders.headers.Authorization,
        selectedCategories,
        flyerItems.total,
      ));
    showLoadingScreen(false);
  };

  const onImportExcelClick = () => {
    setOpenImportModal(true);
  };

  const handleImportWithCategories = async (
    selectedCategories: string[],
    file: File,
  ) => {
    setOpenImportModal(false);
    showLoadingScreen(true);
    if (flyerItemListId && state?.gfAdminUser) {
      const importSuccess = await importExcelFn(
        file,
        flyerItemListId,
        selectedCategories,
      );

      if (importSuccess) {
        // Refresh the flyer items to show the newly imported data
        await fetchFlyerItems(initialGridPaginationModel, []);
      }
    }

    showLoadingScreen(false);
  };

  const handleToolClick = async () => {
    if (!flyerItemListId) return;

    showLoadingScreen(true);
    try {
      const response =
        await adminApiService.gfFlyerItemAdminControllerBulkCalculateSave({
          flyerItemListId: flyerItemListId,
        });

      if (response.success !== false) {
        const relevantSkippedCount = response.details?.skipped?.filter(
          (item) => item.reason !== "no_changes_needed",
        );

        toastSuccess(
          `Operation completed: (${response.total} total) ${response.updated} updated, ${response.added} added, ${response.removed} removed`,
        );

        if (relevantSkippedCount.length) {
          relevantSkippedCount.map((val) => {
            toastSuccess(
              "Skipped Reason: " + val.reason + " for flyer item: " + val.name,
            );
          });
        }
      } else {
        toastError(response.message || "Operation failed");
      }

      await fetchFlyerItems(paginationModel, sortModel);
      setOpenToolsModal(false);
    } catch (error) {
      toastError("Failed to process items. Please try again later.");
      console.error("Tool operation error:", error);
    } finally {
      showLoadingScreen(false);
    }
  };

  const OnProgressComplete = () => {
    setFlyerGenerationInProgress(undefined);
  };

  const handleModalConfirmation = async () => {
    if (isDeleteFlow) {
      handleFlyerItemDelete();
      return;
    }
    await handleModalConfirmationFromHook({
      generateFlyers,
      pendingTemplate,
    });
  };

  const handleCoordinateBatchSave = async (changes: CoordinateChange[]) => {
    showLoadingScreen(true);
    try {
      await processBatchSave({
        changes,
        flyerItemListData,
        authHeaders,
        flyerItemListId,
      });

      // Refresh flyer items to show updated coordinates and images
      await fetchFlyerItems(paginationModel, sortModel);

      // Refresh all flyer items for coordinate editor
      await refetchAllFlyerItems();
    } catch (error) {
      console.error("Error saving coordinates:", error);
      toastError("Failed to save coordinates. Please try again.");
      throw error;
    } finally {
      showLoadingScreen(false);
    }
  };

  const handleImageTagSaved = useCallback((updatedImage: GfImage) => {
    setGfImageData((prev) => ({
      ...prev,
      gfImageList: prev.gfImageList?.map((img) =>
        img.id === updatedImage.id
          ? { ...img, imageTags: updatedImage.imageTags }
          : img,
      ),
    }));
    setUpdateFlyerItem((prev) => {
      if (!prev?.gfMultiImages) return prev;
      return {
        ...prev,
        gfMultiImages: prev.gfMultiImages.map((mi) =>
          mi.gfImage.id === updatedImage.id
            ? {
                ...mi,
                gfImage: { ...mi.gfImage, imageTags: updatedImage.imageTags },
              }
            : mi,
        ),
      };
    });
    setFlyerItems((prev) => ({
      ...prev,
      rows: prev.rows?.map((row) => ({
        ...row,
        gfMultiImages: row.gfMultiImages?.map((mi) =>
          mi.gfImage?.id === updatedImage.id
            ? {
                ...mi,
                gfImage: { ...mi.gfImage, imageTags: updatedImage.imageTags },
              }
            : mi,
        ),
      })),
    }));
  }, []);
  const handleCheckCoordinateJobStatus = async () => {
    if (!flyerItemListData?.flyer?.id) return true;

    try {
      const response: CoordinateJobStatusResponseDto =
        await adminApiService.gfFlyerAdminControllerGetFlyerCoordinateJobStatus(
          flyerItemListData.flyer.id,
          authHeaders,
        );

      // If job is in progress, show modal and prevent opening editor
      if (response.hasActiveJob) {
        setJobInProgressModal(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking coordinate job status:", error);
      // Allow opening if check fails
      return true;
    }
  };

  const handleOpenCoordinateEditor = async () => {
    const canOpen = await handleCheckCoordinateJobStatus();
    if (!canOpen) return;

    const sessionKey = `flyerCoordRefetch_${flyerItemListId}`;
    if (safeSessionStorageGetItem(sessionKey)) {
      safeSessionStorageRemoveItem(sessionKey);
      await fetchFlyerItemListDetails();
      resetAllFlyerItems();
    }

    setOpenResizableWindow(true);
    setActiveWindow("resizable");
  };

  const handleRetryStarted = (jobId: string) => {
    console.log("Coordinate extraction retry started with job ID:", jobId);
    if (flyerItemListId) {
      safeSessionStorageSetItem(`flyerCoordRefetch_${flyerItemListId}`, "true");
    }
    setOpenResizableWindow(false);
    setActiveWindow(null);
  };

  const SearchInputElement = React.useMemo(
    () => (
      <PermissionComponentWrapper
        requiredPermission={["create"]}
        requiredPermissionGroup={["flyer"]}
        wrapperCss={`
        display:block;
        margin-bottom:5px;
        margin-top: 5px;
        `}
      >
        <SearchInputWithAdvanced
          onSearch={handleOnImageSearchClick}
          dataTestId="search-input"
          clearSearchText={clearSearch}
          placeHolder={`${t(messages.SEARCH_FOR_IMAGE())}`}
          autoFocus={true}
          enableKeywordSuggestions={true}
          enableAdvancedSearch={true}
        />
      </PermissionComponentWrapper>
    ),
    [clearSearch, handleOnImageSearchClick, t],
  );

  useEffect(() => {
    (async function () {
      showLoadingScreen(true);
      if (updateFlyerItem?.flyerItemList?.id !== flyerItemListId) {
        await fetchFlyerItemListDetails();
      }
      const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
      if (hasActiveFilters) {
        await fetchFlyerItemsWithFilter(filterModel);
      } else {
        await fetchFlyerItems(paginationModel, sortModel);
      }
      view_createFlyer_page();
      showLoadingScreen(false);
    })();
  }, [flyerItemListId, paginationModel, sortModel, filterModel]);

  // Optimized useEffect with individual filter dependencies to prevent unnecessary re-renders
  useEffect(() => {
    handleImageSearch(
      searchData?.search ?? "",
      gfImageData.page,
      advancedSearchConfig.searchLimit,
      filtersState.category,
      filtersState.format,
      filtersState?.size,
      filtersState?.date,
      searchData?.advancedConfig,
    );
  }, [
    searchData?.search,
    searchData?.advancedConfig,
    gfImageData.page,
    advancedSearchConfig.searchLimit,
    filtersState.category,
    filtersState.format,
    filtersState.size,
    filtersState.date,
  ]);

  return (
    <Container data-testid="create-flyer-item-page">
      {!flyerItemListData ? (
        <> </>
      ) : (
        <>
          <DateTableWrapper>
            {flyerItems?.rows?.length ||
            muiFilterModelHasActiveItems(filterModel) ? (
              <Box
                ref={splitPaneRef}
                data-testid="create-flyer-item-split-pane"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  width: "100%",
                  alignItems: "stretch",
                  gap: { xs: 1, md: 0 },
                }}
              >
                <Box
                  sx={{
                    width: {
                      xs: "100%",
                      md: `calc(${leftPaneRatio * 100}% - 4px)`,
                    },
                    minWidth: { md: `${minLeftPaneWidth}px` },
                    order: { xs: 2, sm: 1 },
                    overflow: "hidden",
                  }}
                >
                  <TopApp>
                    <LeftSideTop>
                      {!flyerItems?.rows?.length &&
                      !muiFilterModelHasActiveItems(filterModel) &&
                      flyerItemListData?.id ? (
                        <>
                          {flyerItemListData?.jobTestingRecord && (
                            <Stack
                              direction="row"
                              justifyContent="center"
                              sx={{ mt: 1, mb: 1 }}
                            >
                              <Chip
                                label={flyerItemListData.jobTestingRecord}
                                size="small"
                                color="info"
                                sx={{
                                  fontSize: "11px",
                                  height: "22px",
                                  backgroundColor: "#f58220",
                                  color: "#fff",
                                  fontWeight: "500",
                                }}
                              />
                            </Stack>
                          )}
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            width="100%"
                          >
                            <PermissionComponentWrapper
                              requiredPermission={["create"]}
                              requiredPermissionGroup={["flyer"]}
                              wrapperCss="flex: 1;"
                            >
                              <ButtonComponent
                                testId="import-excel-empty-state"
                                buttonStyleCss="
                                  width: 100%;
                                  margin-top: 10px;
                                  padding: 8px 15px;"
                                title={`${t(messages.Import_Excel())}`}
                                onClick={async () => {
                                  onImportExcelClick();
                                }}
                              ></ButtonComponent>
                            </PermissionComponentWrapper>
                            <PermissionComponentWrapper
                              requiredPermission={["create"]}
                              requiredPermissionGroup={["flyer"]}
                              wrapperCss="flex: 1;"
                            >
                              <ButtonComponent
                                testId="download-flyers-empty-state"
                                buttonStyleCss="
                                width: 100%;
                                margin-top: 10px;
                                padding: 8px 15px;"
                                title="Download Flyers"
                                onClick={async () => {
                                  setOpenDownloadFlyers(true);
                                }}
                              ></ButtonComponent>
                            </PermissionComponentWrapper>
                          </Stack>
                        </>
                      ) : (
                        <></>
                      )}
                    </LeftSideTop>
                    <LeftButtonSection>
                      {flyerItemListData && (
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          spacing={{ xs: 2, md: 2 }}
                        >
                          <MerchantInfoSection
                            flyerItemListData={flyerItemListData}
                            totalProducts={flyerItems?.total}
                            showTotalProducts={Boolean(
                              flyerItems?.rows?.length,
                            )}
                            hasFlyer={Boolean(flyerItemListData?.flyer)}
                            onOpenCoordinateEditor={handleOpenCoordinateEditor}
                          />

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "stretch", sm: "flex-start" }}
                            justifyContent={{
                              xs: "flex-start",
                              sm: "flex-end",
                            }}
                            spacing={0}
                            sx={{
                              width: { xs: "100%", sm: "auto" },
                              rowGap: { xs: 1, sm: 1 },
                              columnGap: { xs: 0, sm: 1 },
                              flexWrap: "wrap",
                              justifyContent: {
                                xs: "flex-start",
                                sm: "flex-end",
                              },
                              alignContent: "flex-start",
                              ml: { sm: "auto" },
                              maxWidth: { sm: "100%", md: 420 },
                            }}
                          >
                            <Tooltip
                              title={t(
                                messages.Export_Selected_Items_To_Excel(),
                              )}
                            >
                              <Button
                                variant="outlined"
                                startIcon={
                                  <SimCardDownloadIcon
                                    sx={{
                                      fontSize: { xs: "18px", sm: "20px" },
                                    }}
                                  />
                                }
                                disabled={!rowSelectionModel?.length}
                                onClick={handleExportSelectedFlyerItems}
                                sx={{
                                  ...actionButtonSx,
                                  color: "#ff8038",
                                  borderColor: "rgba(255, 128, 56, 0.5)",
                                  "&:hover": {
                                    borderColor: "#ff8038",
                                    backgroundColor: "rgba(255, 128, 56, 0.04)",
                                  },
                                }}
                              >
                                {t(messages.Export_to_Excel())}
                              </Button>
                            </Tooltip>
                            <PermissionComponentWrapper
                              requiredPermission={["delete"]}
                              requiredPermissionGroup={["flyer"]}
                            >
                              <Button
                                variant="outlined"
                                startIcon={
                                  <DeleteIcon
                                    sx={{
                                      fontSize: { xs: "18px", sm: "20px" },
                                    }}
                                  />
                                }
                                disabled={!rowSelectionModel?.length}
                                onClick={handleOpenDelete}
                                color="error"
                                sx={{
                                  ...actionButtonSx,
                                  border: "1px solid rgba(211, 47, 47, 0.5)",
                                  "&:hover": {
                                    border: "1px solid #d32f2f",
                                    backgroundColor: "rgba(211, 47, 47, 0.04)",
                                  },
                                }}
                              >
                                {t(messages.Delete())}
                              </Button>
                            </PermissionComponentWrapper>
                          </Stack>
                        </Stack>
                      )}
                    </LeftButtonSection>
                  </TopApp>
                  <LinearDeterminate
                    totalCount={flyerGenerationInProgress}
                    flyerItemListId={flyerItemListId}
                    onComplete={OnProgressComplete}
                    figmaJobId={jobId}
                  ></LinearDeterminate>
                  <ListTableQueryPagination
                    loadFailed={listRequestGuard.loadFailed}
                    isLoading={isTableLoading}
                    columns={tableColumns}
                    rows={flyerItems?.rows}
                    total={flyerItems?.total}
                    paginationModel={paginationModel}
                    sortModel={sortModel}
                    handleRowSelection={(value) => {
                      setRowSelectionModel(value);
                    }}
                    onTableChange={handleTableChange}
                    dynamicHeightAdjuster={250}
                    onExcelExportClick={exportExcel}
                    onImportExcelClick={onImportExcelClick}
                    onGenerateFlyer={handleGenerateFlyers}
                    onDownloadFLyer={() => {
                      setOpenDownloadFlyers(true);
                    }}
                    onToolsClick={handleToolsClick}
                    onStatisticsClick={handleStatisticsClick}
                    filterModel={filterModel}
                    onFilterModelChange={handleFlyerItemFilterModelChange}
                  />
                </Box>

                <Box
                  role="separator"
                  aria-label="Resize panes"
                  aria-orientation="vertical"
                  data-testid="create-flyer-item-split-pane-resizer"
                  onMouseDown={handleResizeStart}
                  onTouchStart={handleResizeTouchStart}
                  sx={{
                    width: "18px",
                    display: { xs: "none", md: "flex" },
                    order: { xs: 0, sm: 2 },
                    alignSelf: "stretch",
                    cursor: "col-resize",
                    position: "relative",
                    justifyContent: "center",
                    alignItems: "center",
                    userSelect: "none",
                    touchAction: "none",
                    zIndex: 2,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      width: "2px",
                      height: "calc(100% - 2px)",
                      top: 0,
                      left: "50%",
                      transform: "translateX(-50%)",
                      borderRadius: "999px",
                      backgroundColor: "#c9c9c9",
                    },
                    "&:hover::before": {
                      backgroundColor: "#ff8038",
                    },
                  }}
                >
                  <HeightIcon
                    sx={{
                      fontSize: 24,
                      color: "#ff8038",
                      transform: "rotate(90deg)",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    width: {
                      xs: "100%",
                      md: `calc(${(1 - leftPaneRatio) * 100}% - 4px)`,
                    },
                    minWidth: { md: `${minRightPaneWidth}px` },
                    order: { xs: 1, sm: 3 },
                    padding: { xs: "0px", sm: "10px", md: "10px" },
                    paddingTop: { xs: "0px", sm: "0px", md: "0px" },
                    marginBottom: { xs: "16px", sm: "0px" },
                    overflow: "hidden",
                  }}
                >
                  <PermissionComponentWrapper
                    requiredPermission={["create"]}
                    requiredPermissionGroup={["flyer"]}
                    wrapperCss={`
                    display: flex;
                    flex-direction: column;
                    @media (min-width: 900px) {
                      height: calc(100vh - 155px);
                    }
                    `}
                  >
                    <LeftSideBottom>
                      <PermissionComponentWrapper
                        requiredPermission={["create"]}
                        requiredPermissionGroup={["flyer"]}
                        wrapperCss={`
                        display: block;
                        flex-shrink: 0;
                      `}
                      >
                        <FilterBar
                          filterListItems={selectList}
                        ></FilterBar>{" "}
                      </PermissionComponentWrapper>
                      <Box sx={{ flexShrink: 0 }}>
                        {flyerItemListData?.id ? SearchInputElement : <></>}
                      </Box>
                      <SideFlyerGridList
                        itemsList={gfImageData?.gfImageList}
                        onItemClick={handleOnImageItemClick}
                        dataTestId="gfImage-sideContainer"
                        hasMore={rightSideHasMoreGfImage}
                        getMore={handleRightSidePageChangeScroll}
                        isLoading={isRightSideImageLoading}
                        searchText={searchData?.search}
                        shouldRenderCreateFlyerButton={true}
                        onTagEdit={(image) => setImageForTagEdit(image)}
                      />

                      <CreateFlyerButtonWrapper
                        isVisible={Boolean(
                          gfImageData?.gfImageList?.length &&
                          searchData?.search?.length,
                        )}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: "6px",
                            width: "100%",
                            p: "6px",
                            backgroundColor: "#f5f5f5",
                            borderBottomLeftRadius: "10px",
                            borderBottomRightRadius: "10px",
                          }}
                        >
                          <Tooltip
                            title={`${t(messages.Create_New_Flyer_Item_With_No_Placeholder_Image())}`}
                            arrow
                          >
                            <Button
                              data-testid="create-flyer-item-button"
                              variant="contained"
                              startIcon={<AddCircleOutlineIcon />}
                              onClick={async () => {
                                const date = new Date();
                                handleOnImageItemClick({
                                  id: "",
                                  src: "",
                                  createdAt: date,
                                  updatedAt: date,
                                  isDeleted: false,
                                });
                              }}
                              sx={{
                                flex: 1,
                                py: 1,
                                backgroundColor: "#ff8038",
                                color: "#fff",
                                fontSize: "12px",
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: "6px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                                "&:hover": {
                                  backgroundColor: "#e06e2c",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                },
                              }}
                            >
                              {t(messages.Create_Item_From_local())}
                            </Button>
                          </Tooltip>
                          <Tooltip
                            title={`${t(messages.Create_New_Flyer_Item_With_Placeholder_Image())}`}
                            arrow
                          >
                            <Button
                              data-testid="create-flyer-item-no-image-button"
                              variant="contained"
                              startIcon={<HideImageOutlinedIcon />}
                              onClick={() =>
                                handleOnImageItemClick(NO_IMAGE_AVAILABLE)
                              }
                              sx={{
                                flex: 1,
                                py: 1,
                                backgroundColor: "#ff8038",
                                color: "#fff",
                                fontSize: "12px",
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: "6px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                                "&:hover": {
                                  backgroundColor: "#e06e2c",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                },
                              }}
                            >
                              {t(messages.Create_Item_Without_Image())}
                            </Button>
                          </Tooltip>
                        </Box>
                      </CreateFlyerButtonWrapper>
                    </LeftSideBottom>
                  </PermissionComponentWrapper>
                </Box>
              </Box>
            ) : (
              <PermissionComponentWrapper
                requiredPermission={["create"]}
                requiredPermissionGroup={["flyer"]}
                wrapperCss={`
                    display:block;
                      `}
              >
                <LeftSideBottom>
                  <Stack marginBottom={2}>
                    <MerchantInfoSection
                      flyerItemListData={flyerItemListData}
                      totalProducts={flyerItems?.total}
                      showTotalProducts={Boolean(flyerItems?.rows?.length)}
                      hasFlyer={Boolean(flyerItemListData?.flyer)}
                      onOpenCoordinateEditor={handleOpenCoordinateEditor}
                    />
                    {!flyerItems?.rows?.length && flyerItemListData?.id && (
                      <>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          width="100%"
                          alignItems={"flex-start"}
                        >
                          <PermissionComponentWrapper
                            requiredPermission={["create"]}
                            requiredPermissionGroup={["flyer"]}
                          >
                            <ButtonComponent
                              testId="import-excel-empty-state"
                              buttonStyleCss="
                          padding: 8px 15px;"
                              title={`${t(messages.Import_Excel())}`}
                              onClick={async () => {
                                onImportExcelClick();
                              }}
                            ></ButtonComponent>
                          </PermissionComponentWrapper>
                          <PermissionComponentWrapper
                            requiredPermission={["create"]}
                            requiredPermissionGroup={["flyer"]}
                          >
                            <ButtonComponent
                              testId="download-flyers-empty-state"
                              buttonStyleCss="
                          padding: 8px 15px;"
                              title="Download Flyers"
                              onClick={async () => {
                                setOpenDownloadFlyers(true);
                              }}
                            ></ButtonComponent>
                          </PermissionComponentWrapper>
                        </Stack>
                      </>
                    )}
                  </Stack>
                  {flyerItemListData?.id ? SearchInputElement : <></>}
                  <FlyerGridList
                    searchText={searchData?.search}
                    itemsList={gfImageData?.gfImageList}
                    rowsPerPage={advancedSearchConfig.searchLimit}
                    count={
                      advancedSearchConfig.searchLimit && gfImageData?.total
                        ? Math.ceil(
                            gfImageData?.total /
                              advancedSearchConfig.searchLimit,
                          )
                        : 10
                    }
                    page={gfImageData.page}
                    handlePaginationChange={handlePageChange}
                    onItemClick={handleOnImageItemClick}
                    dataTestId="gfImage-container"
                    isLoading={isRightSideImageLoading}
                    onTagEdit={(image) => setImageForTagEdit(image)}
                  />
                </LeftSideBottom>
              </PermissionComponentWrapper>
            )}
          </DateTableWrapper>
          {flyerItemListId && (
            <GfGeneratedFlyerImagesModal
              flyerItemListId={flyerItemListId}
              open={openDownloadFlyers}
              callbackCloseOpen={() => {
                setOpenDownloadFlyers(false);
              }}
            />
          )}

          {openCreateFlyer && (
            <CreateFlyerModalWithMetaData
              updateFlyerItem={updateFlyerItem}
              setUpdateFlyerItem={setUpdateFlyerItem}
              onImageTagSaved={handleImageTagSaved}
              onClick={() => setActiveWindow("modal")}
              zIndex={activeWindow === "modal" ? 1400 : 1300}
              isResizableWindowOpen={openResizableWindow}
              onSaveFlyerItem={(savedItem?: GfFlyerItemDto) => {
                setOpenCreateFlyer(false);
                fetchFlyerItems(paginationModel, sortModel);

                // If there's a callback (from coordinate viewer creating a new item), call it
                if (createItemCallback && savedItem) {
                  createItemCallback(savedItem);
                  setCreateItemCallback(null);
                  // For new items created from coordinate editor, do a full refetch
                  // so the selection dialog has the latest data
                  refetchAllFlyerItems();
                } else if (savedItem?.id && openResizableWindow) {
                  // For edits while coordinate editor is open, update only the
                  // changed item in-place to preserve unsaved coordinate changes
                  updateAllFlyerItem(savedItem);
                } else {
                  // Fallback: full refetch when coordinate editor is not open
                  refetchAllFlyerItems();
                }

                // Clear search results if keepSearchResults is false
                if (!advancedSearchConfig.keepSearchResults) {
                  setSearchData({
                    search: "",
                  });
                  setClearSearch(true);
                }
                setActiveWindow(openResizableWindow ? "resizable" : null);
              }}
              handleOnBackgroundOverlayClick={() => {
                handleOnCloseFlyer();
              }}
              navigationDirection={navigationDirection}
              canNavigatePrevious={currentFlyerItemIndex > 0}
              canNavigateNext={
                currentFlyerItemIndex >= 0 && flyerItems.rows
                  ? currentFlyerItemIndex < flyerItems.rows.length - 1
                  : false
              }
              currentItemIndex={currentFlyerItemIndex}
              totalItems={flyerItems.rows?.length || 0}
              onNavigatePrevious={handleNavigateToPrevious}
              onNavigateNext={handleNavigateToNext}
            ></CreateFlyerModalWithMetaData>
          )}
          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleModalConfirmation}
            title={confirmationModal.title}
            description={confirmationModal.description}
            warningMessage={confirmationModal.warningMessage}
            warningSeverity={confirmationModal.warningSeverity}
            confirmButtonColor="#ff8038"
          />
          <ExportCategoriesModal
            open={openExportModal}
            onClose={() => setOpenExportModal(false)}
            onExport={handleExportWithCategories}
            categories={flyerItemCategories}
            flyerItemListId={flyerItemListId}
          />
          <ImportCategoriesModal
            open={openImportModal}
            onClose={() => setOpenImportModal(false)}
            onImport={handleImportWithCategories}
            categories={flyerItemCategories}
          />
          <ToolsModal
            open={openToolsModal}
            onClose={() => setOpenToolsModal(false)}
            handleToolClick={handleToolClick}
          />
          <StatisticsModal
            open={openStatisticsModal}
            onClose={() => setOpenStatisticsModal(false)}
            flyerItemListId={flyerItemListId}
          />
          <ImageTagEditModal
            open={Boolean(imageForTagEdit)}
            image={imageForTagEdit}
            onClose={() => setImageForTagEdit(null)}
            onSaved={(updatedImage) => {
              handleImageTagSaved(updatedImage);
              setImageForTagEdit(null);
            }}
          />
          {openTemplateModal && (
            <TemplateSelectionModal
              open={openTemplateModal}
              onClose={() => {
                if (!isGenerating) {
                  setOpenTemplateModal(false);
                }
              }}
              generating={isGenerating}
              onGenerate={(tpl) => generateFlyersForFlyerItemList(tpl)}
              storeIds={flyerItemListData?.stores?.map((s) => s.id) || []}
            />
          )}
          <ResizableDraggableWindow
            open={openResizableWindow}
            onClose={() => {
              setOpenResizableWindow(false);
              setActiveWindow(openCreateFlyer ? "modal" : null);
            }}
            title="Flyer Coordinate Editor"
            titleTooltip={FlyerEditorTooltipDescription}
            initialWidth={900}
            initialHeight={700}
            onSizeChange={(size) => setWindowSize(size)}
            zIndex={activeWindow === "resizable" ? 1400 : 1300}
            onClick={() => setActiveWindow("resizable")}
          >
            {isLoadingAllItems ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <CircularProgress sx={{ color: "#ff8038" }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textTransform: "capitalize" }}
                >
                  Loading flyer items...
                </Typography>
              </Box>
            ) : allFlyerItemsError && allFlyerItems.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={refetchAllFlyerItems}
                    >
                      {t(messages.Try_Again())}
                    </Button>
                  }
                >
                  {t(messages.All_Flyer_Items_Load_Failed())}
                </Alert>
              </Box>
            ) : flyerItemListData?.flyer?.gfImages ? (
              <FlyerCoordinateEditor
                gfImages={flyerItemListData.flyer.gfImages}
                flyerItems={allFlyerItems}
                onSave={handleCoordinateBatchSave}
                onSaveComplete={() => setOpenResizableWindow(false)}
                onRetryStarted={handleRetryStarted}
                containerSize={windowSize}
                flyerId={flyerItemListData.flyer.id}
                flyerItemListId={flyerItemListId}
                isAiGenerated={
                  flyerItemListData?.jobTestingRecord ===
                  JobTestingRecord.AICOORDINATEGENERATION
                }
                onCreateNewItemRequest={(callback) => {
                  // Store the callback and open the create flyer modal
                  setCreateItemCallback(() => callback);
                  setUpdateFlyerItem({
                    flyerItemList: flyerItemListData,
                  });
                  setOpenCreateFlyer(true);
                }}
                onEditItemRequest={(item) => {
                  // Open the create flyer modal with the selected item's data
                  setUpdateFlyerItem(item);
                  setOpenCreateFlyer(true);
                }}
              />
            ) : (
              <Typography variant="body1" sx={{ p: 3, textAlign: "center" }}>
                No flyer images available. Please ensure the flyer has been
                generated.
              </Typography>
            )}
          </ResizableDraggableWindow>

          <MuiConfirmationModal
            open={jobInProgressModal}
            handleCancel={() => setJobInProgressModal(false)}
            title={t(messages.Coordinate_Job_In_Progress_Title())}
            description={t(messages.Coordinate_Job_In_Progress_Description())}
            warningMessage={t(messages.Coordinate_Job_In_Progress_Warning())}
            warningSeverity="info"
            showConfirmButton={false}
          />
        </>
      )}{" "}
    </Container>
  );
};

export default CreateFlyer;
